import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { MentionMember } from "@/lib/mentions";

type Mode = "workspace" | "guest" | "share";

/**
 * useMentionableMembers — members the current commenter may @mention, resolved
 * against the right endpoint for the viewing mode:
 *   - workspace : GET /editor/videos/:id/mentionable        (all workspace members)
 *   - share     : GET /share/:token/videos/:id/mentionable  (project collaborators, guest-safe)
 *   - guest     : GET /watch/:token/mentionable             (project collaborators, guest-safe)
 *
 * The guest/share endpoints are project-scoped + names-only so a link-holder
 * can't enumerate the whole workspace. Returns [] when the share disallows
 * comments; in that case the composer degrades to a plain textarea.
 */
export function useMentionableMembers(opts: {
  mode: Mode;
  videoId: string | null | undefined;
  watchKey: string | undefined;
  enabled?: boolean;
}) {
  const { mode, videoId, watchKey, enabled = true } = opts;

  const path =
    mode === "workspace"
      ? videoId
        ? `/editor/videos/${videoId}/mentionable`
        : null
      : mode === "share"
        ? watchKey && videoId
          ? `/share/${watchKey}/videos/${videoId}/mentionable`
          : null
        : watchKey // guest (legacy /watch/:token) — endpoint uses the token's root
          ? `/watch/${watchKey}/mentionable`
          : null;

  return useQuery<MentionMember[]>({
    queryKey: ["mentionable", mode, watchKey ?? null, videoId ?? null],
    queryFn: () => api.get<MentionMember[]>(path as string),
    enabled: enabled && !!path,
    staleTime: 60_000,
  });
}
